import type {
  ArgsProps,
  NotificationInstance,
} from 'antd/lib/notification/interface';
import { App } from 'antd';

type NotificationType = 'success' | 'error' | 'info' | 'warning' | 'open';

type Notifiers = {
  readonly [key in NotificationType]: (message: string) => void;
};

// Where in the DOM the notification will appear
const placement = 'bottomRight';

const props = (message: string): ArgsProps => ({
  message,
  placement,
  duration: 2,
});

const caller =
  (api: NotificationInstance) =>
  (notificationType: NotificationType) =>
  (message: string) =>
    api[notificationType](props(message));

/**
 * Takes a notification instance and returns a set of notification functions
 */
const notifier = (api: NotificationInstance): Notifiers => {
  const notify = caller(api);
  return {
    success: notify('success'),
    error: notify('error'),
    info: notify('info'),
    warning: notify('warning'),
    open: notify('open'),
  };
};

/**
 * A hook to provide access to the AntD Notification api, returning the
 * actual notification function and the context alongside which that function
 * must be called.
 */
export const useNotify = () => {
  const { notification } = App.useApp();
  return { notify: notifier(notification) };
};
